import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, LayoutGrid, List, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import MeetingWizard from './MeetingWizard';
import { useExcomMeetings, useDeleteExcomMeeting } from '../../hooks/useApi';
import { downloadCSV } from '../../utils/csvExport';
import {
    Avatar,
    Badge,
    Button,
    Card,
    ConfirmDialog,
    DataTable,
    Dropdown,
    EmptyState,
    MenuItem,
    TableToolbar,
} from '../../components/ui';

const STATUS_VARIANT = {
    Approved: 'success',
    Draft: 'neutral',
    Closed: 'primary',
    Pending: 'warning',
};

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

const ExcomMeetings = () => {
    const navigate = useNavigate();
    const [showWizard, setShowWizard] = useState(false);
    const [meetingToEdit, setMeetingToEdit] = useState(null);
    const [meetingToDelete, setMeetingToDelete] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [search, setSearch] = useState('');

    const { data: meetingsData, isLoading } = useExcomMeetings();
    const deleteMeeting = useDeleteExcomMeeting();

    const allMeetings = useMemo(() => meetingsData?.data || [], [meetingsData]);

    const meetings = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return allMeetings;
        return allMeetings.filter((m) =>
            [m.ref_no, m.title, m.chairperson_name, m.venue].some((v) =>
                v?.toLowerCase().includes(term)
            )
        );
    }, [allMeetings, search]);

    const openEdit = (meeting) => {
        setMeetingToEdit(meeting.id);
        setShowWizard(true);
    };

    const confirmDelete = () => {
        deleteMeeting.mutate(meetingToDelete, { onSettled: () => setMeetingToDelete(null) });
    };

    if (showWizard) {
        return (
            <MeetingWizard
                onCancel={() => {
                    setShowWizard(false);
                    setMeetingToEdit(null);
                }}
                onSave={() => {
                    setShowWizard(false);
                    setMeetingToEdit(null);
                }}
                meetingId={meetingToEdit}
            />
        );
    }

    const columns = [
        {
            key: 'title',
            header: 'Reference & title',
            render: (m) => (
                <Link to={`/excom/${m.id}`} onClick={(e) => e.stopPropagation()} className="block group">
                    <span className="block text-micro font-mono text-ink-500">{m.ref_no}</span>
                    <span className="block font-medium text-ink-900 group-hover:text-primary-600 transition-colors duration-150 truncate max-w-[280px]">
                        {m.title}
                    </span>
                </Link>
            ),
        },
        {
            key: 'meeting_date',
            header: 'Date & time',
            render: (m) => (
                <>
                    <span className="block text-ink-800 tabular whitespace-nowrap">
                        {formatDate(m.meeting_date)}
                    </span>
                    <span className="block text-micro text-ink-500 tabular">
                        {m.start_time} – {m.end_time}
                    </span>
                </>
            ),
        },
        {
            key: 'venue',
            header: 'Venue',
            render: (m) => (
                <>
                    <span className="block text-ink-700 truncate max-w-[160px]">{m.venue || '—'}</span>
                    <span className="block text-micro text-ink-500">{m.meeting_type || 'Excom'}</span>
                </>
            ),
        },
        {
            key: 'chairperson_name',
            header: 'Chairperson',
            render: (m) =>
                m.chairperson_name ? (
                    <span className="flex items-center gap-2">
                        <Avatar name={m.chairperson_name} size="sm" />
                        <span className="text-ink-700 truncate max-w-[130px]">{m.chairperson_name}</span>
                    </span>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            key: 'participant_count',
            header: 'Present',
            align: 'right',
            width: 80,
            render: (m) => <span className="tabular text-ink-700">{m.participant_count || 0}</span>,
        },
        {
            key: 'status',
            header: 'Status',
            render: (m) => (
                <Badge variant={STATUS_VARIANT[m.status] || 'neutral'}>{m.status}</Badge>
            ),
        },
        {
            key: 'actions',
            header: '',
            align: 'right',
            width: 48,
            render: (m) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown label={`Actions for ${m.title}`}>
                        <MenuItem icon={Pencil} onClick={() => openEdit(m)}>
                            Edit meeting
                        </MenuItem>
                        <MenuItem icon={Trash2} danger onClick={() => setMeetingToDelete(m.id)}>
                            Delete meeting
                        </MenuItem>
                    </Dropdown>
                </div>
            ),
        },
    ];

    return (
        <>
            <Card>
                <TableToolbar
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search reference, title, chair…"
                    actions={
                        <>
                            <div className="inline-flex items-center gap-0.5 p-0.5 bg-ink-100 rounded-md">
                                {[
                                    { key: 'list', icon: List, label: 'List view' },
                                    { key: 'grid', icon: LayoutGrid, label: 'Grid view' },
                                ].map(({ key, icon: Icon, label }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setViewMode(key)}
                                        aria-pressed={viewMode === key}
                                        aria-label={label}
                                        title={label}
                                        className={`h-[30px] w-[30px] inline-flex items-center justify-center rounded-sm transition-colors duration-150 ${viewMode === key
                                            ? 'bg-white text-ink-900 shadow-xs'
                                            : 'text-ink-500 hover:text-ink-800'
                                            }`}
                                    >
                                        <Icon size={15} aria-hidden="true" />
                                    </button>
                                ))}
                            </div>

                            <Button
                                icon={Download}
                                onClick={() => downloadCSV(meetings, 'excom-meetings.csv')}
                                disabled={!meetings.length}
                            >
                                Export
                            </Button>

                            <Button variant="primary" icon={Plus} onClick={() => setShowWizard(true)}>
                                New meeting
                            </Button>
                        </>
                    }
                />

                {viewMode === 'list' ? (
                    <DataTable
                        columns={columns}
                        rows={meetings}
                        loading={isLoading}
                        onRowClick={(m) => navigate(`/excom/${m.id}`)}
                        empty={
                            <EmptyState
                                icon={ShieldCheck}
                                title={search ? 'No meetings match this search' : 'No Excom meetings yet'}
                                description={
                                    search
                                        ? 'Try a different reference, title or chairperson.'
                                        : 'Record the first Excom meeting to start tracking decisions and actions.'
                                }
                                action={
                                    search ? (
                                        <Button onClick={() => setSearch('')}>Clear search</Button>
                                    ) : (
                                        <Button variant="primary" icon={Plus} onClick={() => setShowWizard(true)}>
                                            New meeting
                                        </Button>
                                    )
                                }
                            />
                        }
                    />
                ) : (
                    <div className="p-4">
                        {meetings.length ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
                                {meetings.map((m) => (
                                    <Link
                                        key={m.id}
                                        to={`/excom/${m.id}`}
                                        className="card card-hover p-4 flex flex-col gap-3"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="text-micro font-mono text-ink-500">{m.ref_no}</p>
                                                <h3 className="text-md font-semibold text-ink-900 truncate mt-0.5">
                                                    {m.title}
                                                </h3>
                                            </div>
                                            <Badge variant={STATUS_VARIANT[m.status] || 'neutral'}>
                                                {m.status}
                                            </Badge>
                                        </div>

                                        <dl className="text-label text-ink-600 space-y-1">
                                            <div className="flex gap-2">
                                                <dt className="text-ink-500 w-16 shrink-0">Date</dt>
                                                <dd className="tabular">
                                                    {formatDate(m.meeting_date)} · {m.start_time}–{m.end_time}
                                                </dd>
                                            </div>
                                            <div className="flex gap-2">
                                                <dt className="text-ink-500 w-16 shrink-0">Venue</dt>
                                                <dd className="truncate">{m.venue || '—'}</dd>
                                            </div>
                                        </dl>

                                        <div className="flex items-center justify-between gap-2 pt-3 mt-auto border-t border-[var(--divider-color)]">
                                            <span className="flex items-center gap-2 min-w-0">
                                                <Avatar name={m.chairperson_name} size="sm" />
                                                <span className="text-label text-ink-700 truncate">
                                                    {m.chairperson_name || 'No chair'}
                                                </span>
                                            </span>
                                            <span className="text-micro text-ink-500 tabular shrink-0">
                                                {m.participant_count || 0} present
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <EmptyState
                                icon={ShieldCheck}
                                title={search ? 'No meetings match this search' : 'No Excom meetings yet'}
                                description="Record the first Excom meeting to start tracking decisions and actions."
                            />
                        )}
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={Boolean(meetingToDelete)}
                title="Delete Excom meeting"
                message="This permanently removes the meeting record, its minutes and its action points."
                confirmText="Delete meeting"
                variant="danger"
                isLoading={deleteMeeting.isPending}
                onConfirm={confirmDelete}
                onClose={() => setMeetingToDelete(null)}
            />
        </>
    );
};

export default ExcomMeetings;
